import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../helper/apiClient";
import { login as loginApi, logout as logoutApi, bootstrapSession } from "../api/auth";
import { getUserDetails } from "../api/user";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [address, setAddress] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  async function loadMe() {
    const res = await apiFetch("user/me", { method: "GET" }, { auth: true });
    if (!res.ok) return null;
    return res.json();
  }

  async function bootstrap() {
    try {
      const ok = await bootstrapSession(); // refresh-cookie -> access token in memory
      if (ok) {
        const me = await loadMe();
        setUser(me);

          try {
            const details = await getUserDetails(); // returns { ... , address }
            setAddress(details.address); // can be null
            return ok;
          } catch {
            setAddress(null);
          }
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setAuthReady(true);
    }
  }


  async function login(email, password) {
    // your loginApi should set access token in memory + return user or just access
    const maybeUser = await loginApi(email, password);
    // If login endpoint doesn’t return user, fetch /me
    const me = maybeUser ?? (await loadMe());
    setUser(me);
    return me;
  }

  async function logout() {
    try {
      await logoutApi();
    } finally {
      setUser(null);
    }
  }

  const value = useMemo(
    () => ({
      user,
      address,
      authReady,
      isLoggedIn: !!user,
      setUser, // optional (handy)
      login,
      logout,
      refreshMe: async () => {
        const me = await loadMe();
        setUser(me);
        try {
          const details = await getUserDetails();
          setAddress(details.address ?? null);
        } catch {
          setAddress(null);
        }
        return me;
      },
      bootstrap
    }),
    [user, address, authReady]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider />");
  return ctx;
}
